'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

// Two-way email thread for a deal. Chronological transcript plus a composer.
//
// SECURITY: message bodies render as TEXT (white-space: pre-wrap), never as
// HTML. Inbound bodies are prospect-controlled, so rendering them as markup
// would inject attacker-controlled content into an authenticated admin session.

const card = {
  border: '1px solid var(--theme-elevation-150)',
  borderRadius: '8px',
  padding: '16px 20px',
  marginBottom: '24px',
  background: 'var(--theme-elevation-50)',
}

const btn = {
  padding: '6px 14px',
  borderRadius: '6px',
  border: '1px solid var(--theme-elevation-250)',
  background: 'var(--theme-elevation-0)',
  cursor: 'pointer',
  fontSize: '13px',
}

const STATE_LABEL = {
  'awaiting-us': '🔴 Awaiting You',
  'awaiting-them': '⏳ Awaiting Them',
  closed: '✓ Closed',
}

const STATUS_LABEL = {
  queued: 'Queued',
  delivered: 'Delivered',
  bounced: '⚠️ Bounced',
  failed: '⚠️ Failed',
  complained: '⚠️ Spam complaint',
}

function applyTokens(body, deal, ownerName) {
  const name = deal?.name || ''
  return body
    .replaceAll('{{firstName}}', name.split(' ')[0] || '')
    .replaceAll('{{name}}', name)
    .replaceAll('{{company}}', deal?.company || '')
    .replaceAll('{{ownerName}}', ownerName || '')
}

export function InboxPanelClient({ dealId }) {
  const [loading, setLoading] = useState(true)
  const [deal, setDeal] = useState(null)
  const [thread, setThread] = useState(null)
  const [messages, setMessages] = useState([])
  const [snippets, setSnippets] = useState([])
  const [body, setBody] = useState('')
  const [files, setFiles] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState({})
  const [teammates, setTeammates] = useState([])
  const scroller = useRef(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const dealRes = await fetch(`/payload-api/contact-submissions/${dealId}?depth=0`, {
        credentials: 'include',
      })
      const dealDoc = dealRes.ok ? await dealRes.json() : null
      setDeal(dealDoc)

      const threadRes = await fetch(
        `/payload-api/conversation-threads?where[relatedTo.value][equals]=${encodeURIComponent(dealId)}&limit=1&depth=0`,
        { credentials: 'include' },
      )
      const threadDoc = threadRes.ok ? (await threadRes.json())?.docs?.[0] : null
      setThread(threadDoc || null)

      if (threadDoc) {
        const msgRes = await fetch(
          `/payload-api/conversation-messages?where[thread][equals]=${threadDoc.id}&sort=occurredAt&limit=200&depth=1`,
          { credentials: 'include' },
        )
        setMessages(msgRes.ok ? (await msgRes.json())?.docs || [] : [])
      } else {
        setMessages([])
      }

      const snipRes = await fetch(
        '/payload-api/email-snippets?where[active][equals]=true&sort=sortOrder&limit=50&depth=0',
        { credentials: 'include' },
      )
      setSnippets(snipRes.ok ? (await snipRes.json())?.docs || [] : [])

      // Only admin/sales can own a deal thread. Non-admins get a 403 on the
      // users collection (read is isAdminOrSelf), so the select degrades to
      // read-only rather than erroring.
      const usersRes = await fetch(
        '/payload-api/users?where[role][in]=admin,sales&limit=50&depth=0',
        { credentials: 'include' },
      )
      setTeammates(usersRes.ok ? (await usersRes.json())?.docs || [] : [])
    } catch {
      /* best-effort chrome — the deal edit view still works without it */
    } finally {
      setLoading(false)
    }
  }, [dealId])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight
  }, [messages])

  async function send() {
    if (!body.trim() || busy) return
    setBusy(true)
    setError(null)
    try {
      // Upload attachments first, then reference them by id.
      const attachmentIds = []
      for (const file of files) {
        const form = new FormData()
        form.append('file', file)
        form.append('_payload', JSON.stringify({ direction: 'outbound' }))
        const res = await fetch('/payload-api/conversation-attachments', {
          method: 'POST',
          credentials: 'include',
          body: form,
        })
        if (!res.ok) throw new Error(`Upload failed for ${file.name}`)
        attachmentIds.push((await res.json())?.doc?.id)
      }

      const res = await fetch('/api/inbox/reply', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dealId, body, attachmentIds }),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json?.error || 'Send failed')
      setBody('')
      setFiles([])
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Send failed')
    } finally {
      setBusy(false)
    }
  }

  async function closeThread() {
    if (!thread) return
    setBusy(true)
    setError(null)
    try {
      const res = await fetch(`/payload-api/conversation-threads/${thread.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state: 'closed' }),
      })
      if (!res.ok) {
        const json = await res.json().catch(() => null)
        throw new Error(json?.errors?.[0]?.message || 'Close failed')
      }
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Close failed')
    } finally {
      setBusy(false)
    }
  }

  // Reassignment writes BOTH levels: the thread owner (who the nudge goes to)
  // and the deal owner (who the never-contacted pass goes to, since that pass
  // fires before any thread exists). Escalation is only actionable if a human
  // can hand a thread over.
  async function reassign(userId) {
    setBusy(true)
    setError(null)
    try {
      // `userId` comes from a native <select>'s onChange, which is always a
      // string (or "" when unassigning). The relationship field on both
      // collections rejects a string id at the REST layer, so it must be
      // coerced to a number before the PATCH — sending "162" 400s where
      // sending 162 succeeds.
      const value = userId ? Number(userId) : null
      if (thread) {
        const threadRes = await fetch(`/payload-api/conversation-threads/${thread.id}`, {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assignedTo: value }),
        })
        if (!threadRes.ok) {
          const json = await threadRes.json().catch(() => null)
          throw new Error(json?.errors?.[0]?.message || 'Reassign failed (thread)')
        }
      }
      const dealRes = await fetch(`/payload-api/contact-submissions/${dealId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignedTo: value }),
      })
      if (!dealRes.ok) {
        const json = await dealRes.json().catch(() => null)
        throw new Error(json?.errors?.[0]?.message || 'Reassign failed (deal)')
      }
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reassign failed')
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return <div style={card}>Loading conversation…</div>
  }

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <strong style={{ fontSize: 15 }}>Conversation</strong>
        <span style={{ fontSize: 13, color: 'var(--theme-elevation-600)' }}>
          {deal?.name ? `${deal.name} · ` : ''}
          {deal?.email}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 13 }}>
          {thread ? STATE_LABEL[thread.state] : 'No thread yet'}
        </span>
        <select
          style={{ ...btn, maxWidth: 190 }}
          value={String(thread?.assignedTo ?? deal?.assignedTo ?? '')}
          onChange={(e) => reassign(e.target.value)}
          disabled={busy || teammates.length === 0}
          title="Conversation owner — receives the follow-up nudge"
        >
          <option value="">Unassigned</option>
          {teammates.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name || u.email}
            </option>
          ))}
        </select>
        {thread && thread.state !== 'closed' && (
          <button type="button" style={btn} onClick={closeThread} disabled={busy}>
            Close
          </button>
        )}
      </div>

      <div
        ref={scroller}
        style={{
          maxHeight: 480,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          marginBottom: 16,
        }}
      >
        {messages.length === 0 && (
          <p style={{ fontSize: 13, color: 'var(--theme-elevation-500)', margin: 0 }}>
            No messages yet — your first reply starts the thread.
          </p>
        )}
        {messages.map((m) => {
          const outbound = m.direction === 'outbound'
          return (
            <div
              key={m.id}
              style={{
                alignSelf: outbound ? 'flex-end' : 'flex-start',
                maxWidth: '82%',
                border: `1px solid ${outbound ? '#C9A84C' : 'var(--theme-elevation-200)'}`,
                borderRadius: 8,
                padding: '10px 12px',
                background: outbound ? 'var(--theme-elevation-0)' : 'var(--theme-elevation-100)',
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--theme-elevation-500)',
                  marginBottom: 6,
                  display: 'flex',
                  gap: 8,
                }}
              >
                <span>{outbound ? m.author?.name || 'Team' : m.fromEmail}</span>
                <span>{new Date(m.occurredAt).toLocaleString()}</span>
                {outbound && <span>{STATUS_LABEL[m.deliveryStatus] || m.deliveryStatus}</span>}
              </div>

              {/* TEXT, never HTML — see the security note at the top. */}
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.5 }}>
                {expanded[m.id] ? m.bodyText : m.bodyClean || m.bodyText}
              </div>

              {m.bodyText && m.bodyClean && m.bodyText !== m.bodyClean && (
                <button
                  type="button"
                  onClick={() => setExpanded((p) => ({ ...p, [m.id]: !p[m.id] }))}
                  style={{
                    ...btn,
                    padding: '2px 8px',
                    fontSize: 11,
                    marginTop: 6,
                  }}
                >
                  {expanded[m.id] ? 'Hide quoted history' : 'Show quoted history'}
                </button>
              )}

              {(m.attachments || []).length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', fontSize: 12 }}>
                  {m.attachments.map((a) => (
                    <li key={a.id || a}>
                      📎{' '}
                      <a
                        href={`/payload-api/conversation-attachments/file/${encodeURIComponent(a.filename)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {a.filename}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )
        })}
      </div>

      {snippets.length > 0 && (
        <select
          style={{ ...btn, marginBottom: 8, width: '100%' }}
          value=""
          onChange={(e) => {
            const snippet = snippets.find((s) => String(s.id) === e.target.value)
            if (snippet) setBody(applyTokens(snippet.body, deal, ''))
          }}
        >
          <option value="">Insert a snippet…</option>
          {snippets.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      )}

      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') send()
        }}
        placeholder="Write your reply… (⌘+Enter to send)"
        rows={5}
        style={{
          width: '100%',
          padding: 10,
          borderRadius: 6,
          border: '1px solid var(--theme-elevation-250)',
          background: 'var(--theme-elevation-0)',
          fontSize: 14,
          fontFamily: 'inherit',
        }}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
        <input type="file" multiple onChange={(e) => setFiles([...e.target.files])} />
        <button
          type="button"
          style={{ ...btn, background: '#C9A84C', color: '#0a0a0a', fontWeight: 600 }}
          onClick={send}
          disabled={busy || !body.trim()}
        >
          {busy ? 'Sending…' : 'Send reply'}
        </button>
        {error && <span style={{ fontSize: 12, color: 'var(--theme-error-500, #c62828)' }}>{error}</span>}
      </div>
    </div>
  )
}
